import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import WindowVisibilityHelper from './AudioAlerts/WindowVisibilityHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants';
import {
  PUSH_NOTIFICATION_FLAGS,
  canShowBrowserNotification,
  isInboxNotificationEnabled,
  resolveNewMessagePushFlag,
} from './notificationPreferences';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'team.changed': this.onTeamChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'aceleres.equipe_refresh': this.onAceleresEquipeRefresh,
      'aceleres.carteirizacao_request.updated':
        this.onAceleresCarteirizacaoRequestUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
    emitter.emit(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresEquipeRefresh = () => {
    emitter.emit(BUS_EVENTS.ACELERES_EQUIPE_ONLINE_REFRESH);
  };

  // eslint-disable-next-line class-methods-use-this
  onAceleresCarteirizacaoRequestUpdated = data => {
    emitter.emit(BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED, data);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
    this.maybeNotifyAssignedToMe(payload);
  };

  // Transferência de time (sem troca de assignee) também precisa recolocar a
  // conversa na lista/contador de quem ganhou ou perdeu acesso à equipe.
  onTeamChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  // Aceleres: alerta de lead atribuído ao usuário atual. Som e notificação são
  // validados separadamente contra as Preferências de Notificação — o som segue
  // os "Alertas de áudio" e a notificação de desktop segue a categoria
  // "Atribuição de conversa", a permissão do navegador e o filtro por canal.
  maybeNotifyAssignedToMe = payload => {
    try {
      const store = this.app.$store;
      const currentUserId = store.getters?.getCurrentUserID;
      const newAssigneeId = payload?.meta?.assignee?.id ?? payload?.assignee_id;

      if (!currentUserId || !newAssigneeId) return;
      if (Number(newAssigneeId) !== Number(currentUserId)) return;

      const inboxId = payload?.inbox_id;
      const conversationId = payload?.id ?? '';
      if (!isInboxNotificationEnabled(store, inboxId)) return;

      if (
        DashboardAudioNotificationHelper.isAudioAlertEnabledForEvent(
          EVENT_TYPES.ASSIGNED
        )
      ) {
        // Som sutil e único (ping), volume moderado
        const audio = new Audio('/audio/dashboard/ping.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      }

      if (
        canShowBrowserNotification(store, {
          flag: PUSH_NOTIFICATION_FLAGS.CONVERSATION_ASSIGNMENT,
          inboxId,
        })
      ) {
        // eslint-disable-next-line no-new
        new Notification('Novo lead atribuído', {
          body: 'Uma nova conversa foi atribuída a você.',
          icon: '/integrations/icons/aceleres.svg',
          tag: `aceleres-assigned-${conversationId}`,
          requireInteraction: true,
        });
      }
    } catch (e) {
      // silencioso — o alerta nunca deve quebrar o fluxo de atribuição
    }
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const conversationId = Number(
      data?.conversation_id || data?.conversation?.id || 0
    );
    const conversationData = data?.conversation || {};
    const {
      last_activity_at: lastActivityAt,
      unread_count: unreadCount,
    } = conversationData;

    if (!conversationId) return;

    const previousConversation =
      this.app.$store.getters?.getConversationById?.(conversationId);
    const previousUnreadCount = Number(
      previousConversation?.unread_count || 0
    );

    DashboardAudioNotificationHelper.onNewMessage(data);
    this.maybeNotifyDesktopNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
      unreadCount,
    });

    // A conversa pode ter saído da primeira página enquanto o atendente
    // estava em outra caixa. O evento continua válido, mas ADD_MESSAGE não
    // consegue inserir a mensagem sem um item correspondente no store.
    // Busca somente essa conversa, sem recarregar a SPA.
    const conversationInStore =
      this.app.$store.getters?.getConversationById?.(conversationId);
    if (!conversationInStore) {
      this.app.$store
        .dispatch('getConversation', {
          id: conversationId,
          forceRefresh: true,
        })
        .catch(() => {});
    }

    // O ChatList usa este evento para sincronizar cache, ordenação e a
    // contagem de não lidas em segundo plano, sem substituir a página inteira.
    emitter.emit('conversation_realtime_message', {
      conversationId,
      lastActivityAt,
      unreadCount,
      message: data,
      previousUnreadCount,
    });

    if (
      data.private !== true &&
      Number(data.message_type) === MESSAGE_TYPE.INCOMING
    ) {
      this.fetchConversationStats({ force: false, immediate: false });
    }
  };

  // Aceleres: notificação DESKTOP quando chega mensagem nova do cliente e a aba
  // NÃO está visível (segundo plano / sem foco). A exibição depende das
  // Preferências de Notificação do usuário: permissão do navegador ligada,
  // categoria correspondente marcada ("Nova mensagem em conversa atribuída" ou
  // "Criação de conversa", conforme o dono da conversa) e canal habilitado no
  // filtro por caixa de entrada. A tag por conversation_id faz dedupe (só a
  // mais recente por conversa fica visível).
  maybeNotifyDesktopNewMessage = data => {
    try {
      if (WindowVisibilityHelper.isWindowVisible()) return;
      if (data.private === true) return; // ignora notas privadas
      if (Number(data.message_type) !== MESSAGE_TYPE.INCOMING) return; // só do cliente

      const store = this.app.$store;
      const flag = resolveNewMessagePushFlag({
        assigneeId: data?.conversation?.assignee_id,
        currentUserId: store.getters?.getCurrentUserID,
      });

      if (
        !canShowBrowserNotification(store, { flag, inboxId: data?.inbox_id })
      ) {
        return;
      }

      const senderName =
        data.sender?.name ||
        data.conversation?.meta?.sender?.name ||
        'Nova mensagem';
      const preview = String(data.content || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);

      // eslint-disable-next-line no-new
      new Notification(senderName, {
        body: preview || 'Você recebeu uma nova mensagem.',
        icon: '/integrations/icons/aceleres.svg',
        tag: `aceleres-msg-${data.conversation_id || ''}`,
      });
    } catch (_) {
      // silencioso — a notificação nunca deve quebrar o fluxo de mensagens
    }
  };

  // As atualizações de conversa já chegam como eventos individuais. Recarregar
  // toda a SPA aqui descarta a lista e a conversa aberta sem necessidade.
  // eslint-disable-next-line class-methods-use-this
  onReload = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = (options = {}) => {
    emitter.emit('fetch_conversation_stats', options);
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
